/* eslint-disable react/jsx-no-target-blank */
import {
  Button,
  Chip,
  Col,
  Container,
  IconButton,
  IconButtonProps,
  Row,
  SearchAutocompleteRootElement,
  SearchElement,
  SearchIcon,
  UserIcon,
} from '@farol-ds/react'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import {
  COMPONENT_CLASS,
  COMPONENT_NAME,
  GA4ClickEvent,
  GA4ViewItemListEvent,
  SELECTED_OPTION,
  storeComponentOriginForNextPage,
} from '@jusbrasil-web/shared-ga4-events'
import {
  AutocompleteOption,
  SERPAutocomplete,
  SERPAutocompleteProps,
} from '@jusbrasil-web/shared-serp-autocomplete'
import { setJusbrasilCookie } from '@jusbrasil-web/shared-utils-cookies'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { capitalizePhrase } from '@jusbrasil-web/shared-utils-format'
import { normalizeText } from '@jusbrasil-web/shared-utils-validators'
import { FC, ReactNode, useContext, useEffect, useRef } from 'react'

import { SHOW_JUSIA_ON_TOPBAR_BASED_ON_SUBSCRIPTION } from '../../constants'
import { TopbarContext } from '../../context'
import { SubscribeLink, TopbarUserMenuProps } from '../../types'
import { TopbarBrand } from '../topbar-brand'
import { TopbarJusIAButton } from '../topbar-jus-ia-button'
import { TopbarJusIAFreemiumButton } from '../topbar-jus-ia-freemium-button'
import { TopbarUserMenu } from '../topbar-user-menu'
import { ThemeToggle } from './theme-toggle'
import styles from './topbar-header.module.scss'

export interface TopbarHeaderProps {
  disableSearch?: boolean
  disableCta?: boolean
  userMenuProps?: TopbarUserMenuProps
  searchProps?: {
    q?: string
    section?: string
    autocompleteProps?: SERPAutocompleteProps['autocompleteProps']
    onFilterChange?: (value: string) => void
  }
  onCtaClick?: (event: React.MouseEvent, subscribeLink: SubscribeLink) => void
  children?: ReactNode
  searchIconButtonProps?: Partial<IconButtonProps>
}

export enum SearchIntentionOptionEnum {
  UNKNOWN = 'UNKNOWN',
}

export const TopbarHeader: FC<TopbarHeaderProps> = (props) => {
  const {
    disableSearch,
    disableCta,
    userMenuProps,
    searchProps: { autocompleteProps, onFilterChange, ...searchProps } = {},
    onCtaClick,
    children,
    searchIconButtonProps,
  } = props

  const autocompleteRef = useRef<SearchAutocompleteRootElement>(null)
  const searchInputRef = useRef<SearchElement>(null)

  const { logged, user, takeover } = useContext(TopbarContext)
  const subscribeLink = user?.subscribeLink
  const isJusIA = user?.subscribeLink?.isJusIA
  const isAutocompleteSelfSuggestionEnabled = user?.cpfValid

  const { sendGA4Event } = useGA4Dispatcher()

  function handleSearchIconClick(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    autocompleteRef.current?.setAttribute('data-focused', '')
    searchInputRef.current?.focus()
    searchIconButtonProps?.onClick?.(e)
  }

  function onSearchSubmit(_: React.FormEvent<HTMLFormElement> | null, query: string) {
    const now = new Date()
    const in30Days = new Date(now.setDate(now.getDate() + 30))
    setJusbrasilCookie(null, 'from_component_search', 'Topbar', {
      expires: in30Days,
    })

    const relation = SearchIntentionOptionEnum.UNKNOWN
    const clearedQuery = query.replace(/[^\d]+/g, '')

    if (logged && !user?.cpf && clearedQuery.length === 11) {
      const queryId = Buffer.from(clearedQuery).toString('base64')
      const payload = { query: clearedQuery, queryId, relation }

      sessionStorage.setItem('saveSearchInteraction', JSON.stringify(payload))
    }
  }

  const onLinkClick = (e: React.MouseEvent) => {
    const anchorElement = e.target as HTMLAnchorElement
    sendMetrics('Topbar.LinkClicked', user?.pid?.toString(), {
      title: anchorElement && anchorElement.textContent,
      href: anchorElement && anchorElement.href,
      design_system: 'farol',
    })
  }

  const handleSubscriptionClick = (e: React.MouseEvent) => {
    onCtaClick?.(e, subscribeLink || {})
    onLinkClick(e)
  }

  const handleJusIASubscriptionClick = (e: React.MouseEvent) => {
    onCtaClick?.(e, subscribeLink || {})
    onLinkClick(e)
    sendGA4Event(
      new GA4ClickEvent({
        componentClass: COMPONENT_CLASS.TOPBAR,
        componentName: COMPONENT_NAME.TOPBAR_SUBSCRIBE_CTA,
        selectedOption: SELECTED_OPTION.JUS_IA,
      })
    )
    storeComponentOriginForNextPage({
      componentClass: COMPONENT_CLASS.TOPBAR,
      componentName: COMPONENT_NAME.TOPBAR_SUBSCRIBE_CTA,
      selectedOption: SELECTED_OPTION.JUS_IA,
    })
  }

  const handleJusIAFreemiumClick = (e: React.MouseEvent) => {
    onLinkClick(e)
  }

  useEffect(() => {
    if (window?.innerWidth >= 1024 && !disableCta && logged && subscribeLink) {
      sendMetrics('Topbar.LinkViewed', user?.pid?.toString(), {
        title: subscribeLink.label,
        href: `${subscribeLink.url}?fromComponent=topbar-subscribe-cta`,
        design_system: 'farol',
      })
      sendGA4Event(
        new GA4ViewItemListEvent({
          renders: [
            {
              item_brand: COMPONENT_CLASS.TOPBAR,
              item_name: COMPONENT_NAME.TOPBAR_SUBSCRIBE_CTA,
              item_variant: SELECTED_OPTION.JUS_IA,
              item_id: null,
            },
          ],
        })
      )
    }
  }, [disableCta, logged, sendGA4Event, subscribeLink, user?.pid])

  useEffect(() => {
    setJusbrasilCookie(
      null,
      SHOW_JUSIA_ON_TOPBAR_BASED_ON_SUBSCRIPTION.cookie.name,
      isJusIA || false,
      {
        maxAge: SHOW_JUSIA_ON_TOPBAR_BASED_ON_SUBSCRIPTION.cookie.maxAge,
      }
    )
  }, [isJusIA])

  function buildAutocompleteOptions(value: string | undefined, options: AutocompleteOption[]) {
    if (isAutocompleteSelfSuggestionEnabled && value) {
      const searchValue = normalizeText(value)
        ?.normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()

      const isMatchingUserName = !!user?.cpfName?.toLowerCase().includes(searchValue)
      const isMatchingUserCpf = searchValue.length >= 3 && user?.cpf?.startsWith(searchValue)
      const isSuggestingSelfOption = isMatchingUserName || isMatchingUserCpf

      const selfSuggestionOption = {
        id: 0,
        label: capitalizePhrase({ phrase: user?.cpfName || '' }).replace(
          new RegExp(`(${searchValue})`, 'gi'),
          '<strong>$1</strong>'
        ),
        value: '',
        leftIcon: <UserIcon />,
        metadata: {},
        helpText: 'Exibir meus processos',
      }

      if (isSuggestingSelfOption) {
        const updatedOptions = updateAutocompleteOptionsIndex(options)
        const filteredOptions = updatedOptions.filter((option) => option.id !== 0)

        return [...(isSuggestingSelfOption ? [selfSuggestionOption] : []), ...filteredOptions]
      }
    }
    return options
  }

  function updateAutocompleteOptionsIndex(options: AutocompleteOption[]): AutocompleteOption[] {
    return options.map((option) => ({
      ...option,
      id: typeof option.id === 'number' ? option.id + 1 : 0,
    }))
  }

  return (
    <Container asChild>
      <header>
        <Row>
          <Col xs={12} className={styles.root}>
            {children || (
              <>
                {takeover && (
                  <Chip kind="negative" size="md" className={styles.takeover}>
                    Takeover
                  </Chip>
                )}

                <TopbarBrand />

                <span className={styles.separator} />

                {!disableSearch && (
                  <SERPAutocomplete
                    {...searchProps}
                    className={styles.autocompleteRoot}
                    autocompleteProps={{
                      className: styles.autocomplete,
                      ...autocompleteProps,
                    }}
                    autocompleteRef={autocompleteRef}
                    searchRef={searchInputRef}
                    section={searchProps?.section ?? ''}
                    pid={user?.pid?.toString()}
                    onSubmit={onSearchSubmit}
                    customEventName={
                      isAutocompleteSelfSuggestionEnabled ? 'SelfSuggestionClicked' : ''
                    }
                    buildAutocompleteOptions={
                      isAutocompleteSelfSuggestionEnabled ? buildAutocompleteOptions : undefined
                    }
                    cpf={user?.cpf ?? ''}
                    onFilterChange={onFilterChange}
                  />
                )}

                <span className={styles.separator} />

                {!disableCta && logged && (
                  <div className={styles.ctaContainer} data-testid="subscribe-cta-link-container">
                    {/* [WARN] we should keep ctaContainer and Button conditions separated, to avoid CLS during data-fetching on client-side. @andersonba */}
                    {subscribeLink &&
                      (isJusIA ? (
                        <TopbarJusIAButton
                          subscribeLink={subscribeLink}
                          onLinkClick={handleJusIASubscriptionClick}
                        />
                      ) : (
                        <div className={styles.userLeftAddon}>
                          <TopbarJusIAFreemiumButton onClick={handleJusIAFreemiumClick} />

                          <Button kind="secondary" asChild>
                            <a
                              href={`${subscribeLink.url}?fromComponent=topbar-subscribe-cta`}
                              target="_blank"
                              data-testid="topbar-subscribe-cta-link"
                              onClick={handleSubscriptionClick}
                            >
                              {subscribeLink.label}
                            </a>
                          </Button>
                        </div>
                      ))}
                  </div>
                )}

                <ThemeToggle />

                {!disableSearch && (
                  <IconButton
                    id="topbar-search-icon"
                    className={styles.searchButton}
                    size="md"
                    icon={<SearchIcon />}
                    label="Pesquisar"
                    onClick={handleSearchIconClick}
                  />
                )}

                <TopbarUserMenu {...userMenuProps} />
              </>
            )}
          </Col>
        </Row>
      </header>
    </Container>
  )
}
